import React, { memo, useMemo, useCallback } from "react";

const Pagination = memo(function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}) {
  const handlePrevious = useCallback(() => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  }, [currentPage, onPageChange]);

  const handleNext = useCallback(() => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, onPageChange]);

  const handlePageClick = useCallback(
    (pageNumber) => {
      onPageChange(pageNumber);
    },
    [onPageChange],
  );

  // Memoize page numbers calculation
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxPagesToShow = 5;
    const halfMaxPages = Math.floor(maxPagesToShow / 2);

    if (totalPages <= maxPagesToShow + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > halfMaxPages + 2) {
        pages.push("...");
      }

      let startPage = Math.max(2, currentPage - halfMaxPages);
      let endPage = Math.min(totalPages - 1, currentPage + halfMaxPages);

      if (currentPage <= halfMaxPages + 1) {
        endPage = Math.min(totalPages - 1, maxPagesToShow + 1);
      }

      if (currentPage >= totalPages - halfMaxPages) {
        startPage = Math.max(2, totalPages - maxPagesToShow);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - halfMaxPages - 1) {
        pages.push("...");
      }

      pages.push(totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav
      className="flex justify-center text-grayish dark:text-white items-center space-x-1 mt-8"
      aria-label="Pagination"
    >
      {/* Previous Button */}
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        aria-label="Previous page"
        className={`px-3 py-1 border border-cement/50 dark:border-white/50 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out ${
          currentPage === 1
            ? "text-grayish dark:text-white cursor-not-allowed"
            : "text-grayish dark:text-white hover:bg-grayish/10"
        }`}
      >
        &lt;
      </button>

      {/* Page Numbers */}
      {pageNumbers.map((page, index) =>
        typeof page === "number" ? (
          <button
            key={`page-${page}`}
            onClick={() => handlePageClick(page)}
            disabled={currentPage === page}
            aria-current={currentPage === page ? "page" : undefined}
            className={`px-3 py-1 border border-cement/50 dark:border-white/50 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out ${
              currentPage === page
                ? "bg-accent border-accent text-white cursor-default"
                : "text-grayish dark:text-white hover:bg-grayish/10"
            }`}
          >
            {page}
          </button>
        ) : (
          <span
            key={`ellipsis-${index}`}
            className="px-3 py-1 text-sm text-gray-500"
          >
            {page} {/* Ellipsis */}
          </span>
        ),
      )}

      {/* Next Button */}
      <button
        onClick={handleNext}
        disabled={currentPage === totalPages}
        aria-label="Next page"
        className={`px-3 py-1 border border-cement/50 dark:border-white/50 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out ${
          currentPage === totalPages
            ? "text-grayish dark:text-white cursor-not-allowed"
            : "text-grayish dark:text-white hover:bg-grayish/10"
        }`}
      >
        &gt;
      </button>
    </nav>
  );
});

export default Pagination;
